import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Employee } from '../types';

export const useEmployees = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);

  const fetch = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('employees')
        .select('*')
        .order('name');
      if (error) throw error;
      setEmployees(data || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const createEmployee = async (data: Omit<Employee, 'id' | 'created_at' | 'updated_at'>): Promise<void> => {
    const { data: created, error } = await supabase
      .from('employees')
      .insert({ ...data, updated_at: new Date().toISOString() })
      .select()
      .single();
    if (error) throw error;
    if (created) setEmployees(prev => [...prev, created as Employee]);
  };

  const updateEmployee = async (id: number, data: Partial<Employee>): Promise<void> => {
    const { error } = await supabase
      .from('employees')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
    setEmployees(prev => prev.map(e => e.id === id ? { ...e, ...data } : e));
  };

  const deleteEmployee = async (id: number): Promise<void> => {
    const { error } = await supabase.from('employees').delete().eq('id', id);
    if (error) throw error;
    setEmployees(prev => prev.filter(e => e.id !== id));
  };

  return { employees, loading, error, createEmployee, updateEmployee, deleteEmployee, refresh: fetch };
};
